<div class="container-fluid complyon-page reports-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell reports-shell mx-auto">
    <div class="mb-3">
      <h1 class="complyon-title reports-title mb-2 text-l">Reports</h1>
      <p class="complyon-subtitle reports-subtitle mb-0 text-s">Board-ready compliance reporting with evidence-backed findings and executive summaries.</p>
    </div>

    <div class="d-flex flex-column flex-xl-row align-items-start justify-content-between gap-3 mb-3">
      <div class="d-flex flex-wrap align-items-center gap-1" role="tablist" aria-label="Reports tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="btn doa-tab-btn fw-semibold d-inline-flex align-items-center gap-2"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== null" class="doa-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <button
        type="button"
        class="btn btn-link text-decoration-none d-inline-flex align-items-center gap-2 text-secondary p-0"
        @click="reset_sample"
      >
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset decisions</span>
      </button>
    </div>

    <div v-if="active_tab === 'decisions-required'">
      <div class="row g-3 mb-3">
        <div v-for="card in decision_summary_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <div class="complyon-card rounded-4 p-3 h-100">
            <div class="text-uppercase text-xs text-secondary fw-semibold">{{ card.label }}</div>
            <div class="text-l fw-bold mt-1" :class="card.tone === 'danger' ? 'text-danger' : card.tone === 'warning' ? 'text-warning-emphasis' : 'text-success'">{{ card.value }}</div>
            <div class="text-xs text-secondary mt-3">{{ card.note }}</div>
          </div>
        </div>
      </div>

      <div class="complyon-panel rounded-4 overflow-hidden mb-3 bg-info-subtle border border-info-subtle">
        <div class="d-flex align-items-start gap-3 p-3 p-md-4">
          <div class="d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-3 bg-white text-info-emphasis" style="width: 2rem; height: 2rem;">
            <i class="bi bi-sparkles"></i>
          </div>
          <div class="min-w-0">
            <div class="complyon-section-title text-s">Reports show decisions, not just data</div>
            <div class="complyon-section-subtitle text-xs mt-1">Every report on the next tab ends with the calls it needs the board or an executive to make. They are consolidated here as one decision queue.</div>
          </div>
        </div>
      </div>

      <div class="complyon-panel rounded-4 overflow-hidden mb-3">
        <div class="px-3 pt-3">
          <div class="complyon-section-title text-s">Outstanding decisions</div>
          <div class="complyon-section-subtitle text-xs">Sorted by urgency. Decide here, or open the source report for full context.</div>
        </div>

        <div class="px-3 pb-3 pt-3">
          <div class="d-flex flex-column gap-2">
            <div
              v-for="row in decision_queue"
              :key="row.title"
              class="complyon-card rounded-4 p-3"
            >
              <div class="d-flex align-items-start justify-content-between gap-3">
                <div class="min-w-0">
                  <div class="d-flex flex-wrap align-items-center gap-2">
                    <span class="badge rounded-pill bg-danger-subtle text-danger-emphasis text-uppercase">{{ row.severity }}</span>
                    <div class="fw-semibold text-s">{{ row.title }}</div>
                  </div>
                  <div class="mt-2 text-xs">{{ row.summary }}</div>
                  <div class="d-flex flex-wrap align-items-center gap-3 mt-2 text-xs text-secondary">
                    <span class="d-inline-flex align-items-center gap-1">
                      <i class="bi bi-diagram-3"></i>
                      <span>Decided by {{ row.forum }}</span>
                    </span>
                    <span class="d-inline-flex align-items-center gap-1">
                      <i class="bi bi-file-earmark-text"></i>
                      <span>{{ row.source_label }} {{ row.source }}</span>
                    </span>
                  </div>
                </div>
                <span class="badge rounded-pill" :class="row.status_class">{{ row.status }}</span>
              </div>

              <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3 pt-2 border-top">
                <div class="d-flex flex-wrap align-items-center gap-2">
                  <button type="button" class="btn btn-sm config-navbar-primary-action">{{ row.primary_action }}</button>
                  <button
                    v-for="action in row.secondary_actions.filter(Boolean)"
                    :key="`${row.title}-${action}`"
                    type="button"
                    class="btn btn-sm btn-link text-decoration-none text-secondary p-0"
                  >
                    {{ action }}
                  </button>
                </div>
                <button type="button" class="btn btn-link text-decoration-none p-0 d-inline-flex align-items-center gap-1 text-secondary">
                  <i class="bi bi-box-arrow-up-right"></i>
                  <span>Open source</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="complyon-panel rounded-4 overflow-hidden">
        <div class="px-3 pt-3">
          <div class="complyon-section-title text-s">Resolved this cycle</div>
          <div class="complyon-section-subtitle text-xs">Decisions that already have an outcome recorded.</div>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Decision</th>
                <th class="text-xs">Source report</th>
                <th class="text-xs">Forum</th>
                <th class="text-xs">Outcome</th>
                <th class="pe-3 text-xs">Next step</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in resolved_decisions" :key="row.decision">
                <td class="ps-3 fw-semibold">{{ row.decision }}</td>
                <td>{{ row.source_report }}</td>
                <td>{{ row.forum }}</td>
                <td>
                  <span class="badge rounded-pill" :class="row.outcome.class">{{ row.outcome.label }}</span>
                </td>
                <td class="pe-3 text-secondary">{{ row.next_step }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'reports'">
      <div class="row g-3">
        <div v-for="row in report_cards" :key="row.title" class="col-12 col-md-6 col-xl-4">
          <div
            class="complyon-card rounded-4 p-3 h-100 d-flex flex-column"
            role="button"
            tabindex="0"
            @click="open_drawer(row.drawer_key)"
            @keydown.enter="open_drawer(row.drawer_key)"
            @keydown.space.prevent="open_drawer(row.drawer_key)"
          >
            <div class="d-flex align-items-start justify-content-between gap-3">
              <div class="d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-3 bg-info-subtle text-info-emphasis" style="width: 2rem; height: 2rem;">
                <i class="bi" :class="row.icon"></i>
              </div>
              <span class="badge rounded-pill" :class="row.badge_class">{{ row.badge }}</span>
            </div>

            <div class="mt-3 fw-semibold text-s">{{ row.title }}</div>
            <div class="mt-2 text-xs text-secondary">{{ row.subtitle }}</div>

            <div class="mt-auto d-flex align-items-center justify-content-between gap-2 pt-4">
              <div class="text-uppercase text-xs text-secondary">{{ row.updated }}</div>
              <button
                type="button"
                class="btn btn-link text-decoration-none p-0 d-inline-flex align-items-center gap-1 text-secondary fw-semibold"
                :disabled="!row.drawer_key"
                @click.stop="open_drawer(row.drawer_key)"
              >
                <span>{{ row.drawer_key ? 'Generate' : 'Preview' }}</span>
                <i class="bi bi-arrow-up-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'board-packs'">
      <div class="row g-3">
        <div v-for="pack in board_packs" :key="pack.title" class="col-12 col-xl-6">
          <div class="complyon-panel rounded-4 overflow-hidden h-100">
            <div class="d-flex align-items-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4">
              <div class="min-w-0">
                <div class="d-flex align-items-start gap-3">
                  <div class="d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-3 bg-info-subtle text-info-emphasis" style="width: 2rem; height: 2rem;">
                    <i class="bi bi-clipboard-data"></i>
                  </div>
                  <div class="min-w-0">
                    <div class="complyon-section-title text-s">{{ pack.title }}</div>
                    <div class="complyon-section-subtitle text-xs">{{ pack.subtitle }}</div>
                  </div>
                </div>
              </div>
              <span class="badge rounded-pill" :class="pack.status_class">{{ pack.status }}</span>
            </div>

            <div class="px-3 px-md-4 pt-3">
              <div class="d-flex flex-wrap gap-3 text-xs">
                <div v-for="meta in pack.meta" :key="`${pack.title}-${meta.label}`" class="d-inline-flex align-items-center gap-1">
                  <span>{{ meta.label }}</span>
                </div>
              </div>
            </div>

            <div class="px-3 px-md-4 py-3">
              <div class="d-flex flex-column gap-2">
                <div
                  v-for="item in pack.reports"
                  :key="`${pack.title}-${item.title}`"
                  class="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom"
                >
                  <div class="d-flex align-items-center gap-2 min-w-0">
                    <i class="bi bi-file-earmark-text text-info-emphasis flex-shrink-0"></i>
                    <div class="text-truncate">{{ item.title }}</div>
                  </div>
                  <span class="badge rounded-pill bg-light text-secondary border border-light">{{ item.count }}</span>
                </div>
              </div>
            </div>

            <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 px-md-4 pb-3 pb-md-4">
              <div class="d-inline-flex align-items-center gap-2 text-xs text-secondary">
                <i class="bi bi-layout-text-sidebar"></i>
                <span>{{ pack.footer_label }}</span>
              </div>
              <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2">
                <i class="bi bi-download"></i>
                <span>{{ pack.action_label }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'scorecards'">
      <div class="row g-3">
        <div v-for="card in scorecards" :key="card.title" class="col-12 col-md-6 col-xl-4">
          <div class="complyon-card rounded-4 p-3 h-100">
            <div class="d-flex align-items-start justify-content-between gap-3">
              <div>
                <div class="complyon-section-title text-s">{{ card.title }}</div>
                <div class="text-xs text-secondary mt-1">{{ card.owner }}</div>
              </div>
              <div class="d-flex align-items-center gap-2">
                <div class="fw-bold text-l" :class="card.status_tone === 'success' ? 'text-success' : card.status_tone === 'warning' ? 'text-warning-emphasis' : 'text-danger'">{{ card.score }}</div>
                <i v-if="card.trend === 'up'" class="bi bi-arrow-up-right text-success"></i>
                <i v-else-if="card.trend === 'down'" class="bi bi-arrow-down-right text-danger"></i>
                <i v-else class="bi bi-dash text-secondary"></i>
              </div>
            </div>

            <div class="mt-3">
              <div v-for="metric in card.metrics" :key="`${card.title}-${metric.label}`" class="mb-3">
                <div class="d-flex align-items-center justify-content-between gap-2 text-xs mb-1">
                  <span>{{ metric.label }}</span>
                  <span class="text-secondary">{{ metric.value }}</span>
                </div>
                <div class="progress" style="height: 6px;">
                  <div
                    class="progress-bar"
                    :class="metric.tone === 'success' ? 'bg-success' : metric.tone === 'warning' ? 'bg-warning' : 'bg-danger'"
                    role="progressbar"
                    :style="{ width: `${metric.value}%` }"
                    :aria-valuenow="metric.value"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>
            </div>

            <div class="d-flex align-items-center justify-content-between gap-3 mt-3 pt-2 border-top">
              <div class="d-inline-flex align-items-center gap-2 text-xs text-danger">
                <i class="bi bi-exclamation-triangle"></i>
                <span>{{ card.open_items }}</span>
              </div>
              <div class="text-xs text-secondary text-end">{{ card.note }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="active_drawer"
    class="command-centre-drawer-backdrop"
    @click="close_drawer"
  ></div>

  <div
    v-if="active_drawer"
    class="command-centre-drawer"
    role="dialog"
    aria-label="Reports drawer"
  >
    <div class="command-centre-drawer-header border-bottom">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <div class="command-centre-drawer-kicker text-uppercase">{{ active_drawer.section }}</div>
          <h2 class="command-centre-drawer-title mb-1 text-truncate">{{ active_drawer.title }}</h2>
          <div class="command-centre-drawer-subtitle text-truncate">{{ active_drawer.subtitle }}</div>
        </div>
        <div class="d-flex align-items-center gap-2 flex-shrink-0">
          <span class="badge rounded-pill" :class="active_drawer.status_class">{{ active_drawer.status }}</span>
          <button
            type="button"
            class="btn btn-link text-decoration-none p-0 command-centre-drawer-close"
            @click="close_drawer"
            aria-label="Close drawer"
          >
            <i class="bi bi-x-lg fs-5"></i>
          </button>
        </div>
      </div>
    </div>

    <div ref="drawerBody" class="command-centre-drawer-body">
      <div class="row g-2 mb-4">
        <div
          v-for="card in active_drawer.summary_cards"
          :key="card.label"
          class="col-12 col-md-4"
        >
          <div class="complyon-card rounded-4 p-3 text-center h-100">
            <div class="fw-semibold fs-5 lh-1">{{ card.value }}</div>
            <div class="text-uppercase text-muted small mt-1">{{ card.label }}</div>
          </div>
        </div>
      </div>

      <div class="mb-4">
        <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
          <div class="command-centre-drawer-section-title">{{ active_drawer.table.title }}</div>
          <span class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis">{{ active_drawer.table.rows_label }}</span>
        </div>
        <div class="table-responsive">
          <table class="table table-borderless table-sm align-middle mb-0">
            <thead>
              <tr class="border-bottom">
                <th v-for="column in active_drawer.table.columns" :key="column" class="text-uppercase text-muted small fw-semibold py-2">{{ column }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, row_index) in active_drawer.table.rows" :key="row_index" class="border-bottom">
                <td
                  v-for="(cell, cell_index) in row"
                  :key="cell_index"
                  class="py-2"
                >
                  <span
                    v-if="cell && typeof cell === 'object'"
                    class="badge rounded-pill"
                    :class="cell.class"
                  >
                    {{ cell.label }}
                  </span>
                  <span v-else>{{ cell }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        v-for="section in active_drawer.sections"
        :key="section.title"
        class="mb-4"
      >
        <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
          <div class="command-centre-drawer-section-title">{{ section.title }}</div>
          <span
            v-if="section.badge"
            class="badge rounded-pill"
            :class="section.badge_class || 'bg-secondary-subtle text-secondary-emphasis'"
          >
            {{ section.badge }}
          </span>
        </div>
        <div class="bg-dark text-white rounded-3 p-3 small lh-base">{{ section.text }}</div>
      </div>
    </div>

    <div v-if="active_drawer.footer_actions && active_drawer.footer_actions.length" class="command-centre-drawer-footer border-top">
      <div class="d-flex flex-wrap gap-2 align-items-center">
        <div
          v-if="active_drawer_footer_primary_actions.length"
          class="command-centre-drawer-footer-left d-flex align-items-center gap-2"
        >
          <button
            v-for="action in active_drawer_footer_primary_actions"
            :key="action.label"
            type="button"
            class="btn d-inline-flex align-items-center gap-2"
            :class="action.variant === 'primary'
              ? 'config-navbar-primary-action'
              : action.variant === 'secondary'
                ? 'config-navbar-secondary-action'
                : action.variant === 'outline-danger'
                  ? 'btn-outline-danger'
                  : action.variant === 'outline-warning'
                    ? 'btn-outline-warning'
                    : action.variant === 'outline-secondary'
                      ? 'btn-outline-secondary'
                      : 'btn-link text-decoration-none text-secondary px-0'"
          >
            <i class="bi" :class="action.icon"></i>
            <span>{{ action.label }}</span>
          </button>
        </div>

        <div class="command-centre-drawer-footer-actions ms-auto d-flex flex-wrap gap-2 align-items-center">
          <button
            v-for="action in active_drawer_footer_actions"
            :key="action.label"
            type="button"
            class="btn d-inline-flex align-items-center gap-2"
            :class="action.variant === 'primary'
              ? 'config-navbar-primary-action'
              : action.variant === 'secondary'
                ? 'config-navbar-secondary-action'
                : action.variant === 'outline-danger'
                  ? 'btn-outline-danger'
                  : action.variant === 'outline-warning'
                    ? 'btn-outline-warning'
                    : action.variant === 'outline-secondary'
                      ? 'btn-outline-secondary'
                      : 'btn-link text-decoration-none text-secondary px-0'"
          >
            <i class="bi" :class="action.icon"></i>
            <span>{{ action.label }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
